import React, { useState } from "react";
import { plyxApi } from "@/lib/plyxApi";
import { Sparkles, X, Wand2, Hash, Scissors, Loader2, Copy, Crown } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

/**
 * PlyxAIStudio — drawer that surfaces the Plyx Creator AI Pack.
 *
 * Tabs:
 *   ✨ Script Writer    → /plyx/ai/script-writer       (Pro)
 *   📝 Caption Variants → /plyx/ai/caption-variants    (Pro)
 *   ♻️ Remix Ideas     → /plyx/ai/remix-ideas         (Pro)
 *
 * Each tab handles its own loading + Pro paywall toast.
 */
const PlyxAIStudio = ({ open, onClose, presetCaption = "", videoId = null }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("script");

  if (!open) return null;
  const handleProGate = (err) => {
    const detail = err?.response?.data?.detail;
    if (detail?.code === "pro_required") {
      toast.error(`${detail.feature || "This tool"} is a Plyx Pro perk.`, {
        action: { label: "Go Pro", onClick: () => navigate("/plyx/pro") },
        duration: 6000,
      });
      return true;
    }
    return false;
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center sm:justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="plyx-ai-studio-backdrop"
    >
      <div
        className="w-full sm:max-w-lg bg-gradient-to-b from-zinc-900 to-black border-t sm:border border-white/15 sm:rounded-2xl rounded-t-3xl text-white max-h-[88vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="plyx-ai-studio-panel"
      >
        <div className="flex items-center gap-3 px-5 pt-5 pb-3 border-b border-white/10 sticky top-0 bg-zinc-900/95 backdrop-blur-md">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#FFCC2E] to-[#FF3D7F] flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-black" />
          </div>
          <div className="flex-1">
            <div className="font-bold flex items-center gap-2">AI Studio <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded-full bg-gradient-to-r from-[#FFCC2E] to-[#FF3D7F] text-black">Pro</span></div>
            <div className="text-[11px] text-white/60">Write, vary & remix faster</div>
          </div>
          <button
            onClick={onClose}
            className="rounded-full bg-white/10 hover:bg-white/20 p-1.5"
            data-testid="plyx-ai-studio-close"
          ><X className="w-4 h-4"/></button>
        </div>
        <div className="flex gap-2 px-5 pt-3 sticky top-[68px] bg-zinc-900/95 backdrop-blur-md pb-3 z-10 border-b border-white/5">
          {[
            { id: "script",   label: "Script",   icon: Wand2 },
            { id: "variants", label: "Captions", icon: Hash },
            { id: "remix",    label: "Remix",    icon: Scissors },
          ].map((t) => {
            const I = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition ${active ? "bg-white text-black" : "bg-white/10 text-white/70 hover:bg-white/20"}`}
                data-testid={`plyx-ai-studio-tab-${t.id}`}
              >
                <I className="w-3.5 h-3.5"/>{t.label}
              </button>
            );
          })}
        </div>
        <div className="px-5 py-5">
          {tab === "script"   && <ScriptWriterTab onProGate={handleProGate} />}
          {tab === "variants" && <CaptionVariantsTab onProGate={handleProGate} presetCaption={presetCaption} />}
          {tab === "remix"    && <RemixIdeasTab onProGate={handleProGate} videoId={videoId} />}
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block mb-3">
    <div className="text-[11px] uppercase font-bold tracking-wider text-white/60 mb-1">{label}</div>
    {children}
  </label>
);

const Input = (props) => (
  <input
    {...props}
    className={`w-full bg-white/5 border border-white/15 rounded-xl px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#FFCC2E] ${props.className || ""}`}
  />
);

const Textarea = (props) => (
  <textarea
    {...props}
    className={`w-full bg-white/5 border border-white/15 rounded-xl px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#FFCC2E] ${props.className || ""}`}
  />
);

const ScriptWriterTab = ({ onProGate }) => {
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("punchy");
  const [length, setLength] = useState(30);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (topic.trim().length < 4) { toast.error("Add a topic (4+ chars)"); return; }
    setLoading(true);
    try {
      const r = await plyxApi.post("/plyx/ai/script-writer", { topic: topic.trim(), tone, length_sec: Number(length) });
      setResult(r.data);
    } catch (err) {
      if (!onProGate(err)) toast.error("Script writer failed — try again");
    } finally { setLoading(false); }
  };

  const copyScript = () => {
    if (!result) return;
    const text = `${result.hook}\n\n${(result.body||[]).join("\n")}\n\n${result.cta}`;
    navigator.clipboard.writeText(text).then(() => toast.success("Script copied"));
  };

  return (
    <div data-testid="plyx-ai-studio-script">
      <Field label="Topic">
        <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. how to braid box braids in 30 minutes" data-testid="plyx-ai-script-topic"/>
      </Field>
      <Field label="Tone">
        <div className="flex gap-2 flex-wrap">
          {["punchy", "educational", "comedy", "hype", "chill"].map((t) => (
            <button
              key={t} onClick={() => setTone(t)}
              className={`text-xs font-semibold px-3 py-1 rounded-full transition ${tone === t ? "bg-[#FFCC2E] text-black" : "bg-white/10 text-white/70 hover:bg-white/20"}`}
              data-testid={`plyx-ai-script-tone-${t}`}
            >{t}</button>
          ))}
        </div>
      </Field>
      <Field label={`Length: ${length}s`}>
        <input type="range" min="10" max="60" step="5" value={length} onChange={(e) => setLength(e.target.value)} className="w-full" data-testid="plyx-ai-script-length"/>
      </Field>
      <button
        onClick={run} disabled={loading}
        className="w-full bg-gradient-to-r from-[#FFCC2E] to-[#FF3D7F] text-black font-bold rounded-xl py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
        data-testid="plyx-ai-script-run"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Wand2 className="w-4 h-4"/>} Write script
      </button>
      {result && (
        <div className="mt-5 space-y-3" data-testid="plyx-ai-script-result">
          <ResultBlock label="Hook (≤8 words)" body={result.hook} />
          <ResultBlock label="Body" body={(result.body || []).map((b, i) => `${i+1}. ${b}`).join("\n")} multiline />
          <ResultBlock label="CTA" body={result.cta} />
          {result.on_screen_text?.length > 0 && (
            <ResultBlock label="On-screen text" body={result.on_screen_text.map((b, i) => `${i+1}. ${b}`).join("\n")} multiline />
          )}
          <button onClick={copyScript} className="w-full bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl py-2 text-xs flex items-center justify-center gap-2" data-testid="plyx-ai-script-copy">
            <Copy className="w-3.5 h-3.5"/> Copy full script
          </button>
        </div>
      )}
    </div>
  );
};

const CaptionVariantsTab = ({ onProGate, presetCaption }) => {
  const [caption, setCaption] = useState(presetCaption || "");
  const [count, setCount] = useState(3);
  const [loading, setLoading] = useState(false);
  const [variants, setVariants] = useState([]);

  const run = async () => {
    if (caption.trim().length < 4) { toast.error("Type your caption first (4+ chars)"); return; }
    setLoading(true);
    try {
      const r = await plyxApi.post("/plyx/ai/caption-variants", { caption: caption.trim(), count: Number(count) });
      setVariants(r.data?.variants || []);
    } catch (err) {
      if (!onProGate(err)) toast.error("Couldn't generate variants");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="plyx-ai-studio-variants">
      <Field label="Your caption">
        <Textarea value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Original caption…" rows={3} data-testid="plyx-ai-variants-caption"/>
      </Field>
      <Field label={`How many: ${count}`}>
        <input type="range" min="2" max="5" step="1" value={count} onChange={(e) => setCount(e.target.value)} className="w-full" data-testid="plyx-ai-variants-count"/>
      </Field>
      <button
        onClick={run} disabled={loading}
        className="w-full bg-gradient-to-r from-[#FFCC2E] to-[#FF3D7F] text-black font-bold rounded-xl py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
        data-testid="plyx-ai-variants-run"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Hash className="w-4 h-4"/>} Generate {count} variants
      </button>
      {variants.length > 0 && (
        <div className="mt-5 space-y-2" data-testid="plyx-ai-variants-list">
          {variants.map((v, i) => (
            <div key={i} className="rounded-xl bg-white/5 border border-white/10 px-3 py-2 flex items-start gap-2">
              <div className="text-[10px] font-bold uppercase text-[#FFCC2E] mt-0.5">#{i+1}</div>
              <div className="text-sm text-white/90 flex-1">{v}</div>
              <button
                onClick={() => navigator.clipboard.writeText(v).then(() => toast.success("Copied"))}
                className="text-white/40 hover:text-white"
                data-testid={`plyx-ai-variants-copy-${i}`}
              ><Copy className="w-3.5 h-3.5"/></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const RemixIdeasTab = ({ onProGate, videoId }) => {
  const [vid, setVid] = useState(videoId || "");
  const [loading, setLoading] = useState(false);
  const [ideas, setIdeas] = useState([]);

  const run = async () => {
    if (vid.length < 4) { toast.error("Paste a Plyx video ID"); return; }
    setLoading(true);
    try {
      const r = await plyxApi.post("/plyx/ai/remix-ideas", { video_id: vid.trim() });
      setIdeas(r.data?.ideas || []);
    } catch (err) {
      if (!onProGate(err)) toast.error("Couldn't fetch remix ideas");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="plyx-ai-studio-remix">
      <Field label="Plyx video ID to remix">
        <Input value={vid} onChange={(e) => setVid(e.target.value)} placeholder="plv_…" data-testid="plyx-ai-remix-vid"/>
      </Field>
      <button
        onClick={run} disabled={loading}
        className="w-full bg-gradient-to-r from-[#FFCC2E] to-[#FF3D7F] text-black font-bold rounded-xl py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
        data-testid="plyx-ai-remix-run"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Scissors className="w-4 h-4"/>} Get 3 remix angles
      </button>
      {ideas.length > 0 && (
        <div className="mt-5 space-y-2" data-testid="plyx-ai-remix-list">
          {ideas.map((idea, i) => (
            <div key={i} className="rounded-xl bg-white/5 border border-white/10 px-3 py-2 flex items-start gap-2">
              <div className="text-[10px] font-bold uppercase text-[#FFCC2E] mt-0.5">#{i+1}</div>
              <div className="text-sm text-white/90">{idea}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ResultBlock = ({ label, body, multiline = false }) => (
  <div className="rounded-xl bg-white/5 border border-white/10 px-3 py-2">
    <div className="text-[10px] uppercase font-bold tracking-wider text-[#FFCC2E] mb-0.5">{label}</div>
    <div className={`text-sm text-white/90 ${multiline ? "whitespace-pre-line" : ""}`}>{body}</div>
  </div>
);

export default PlyxAIStudio;
